import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Card,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Button,
  Alert,
  Table,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  Badge
} from '@mui/joy';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';
import axiosInstance from '../util/axiosConfig';
import { formatDisplayDate } from '../util/formatDate';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import ResponsiveDetailTable from '../components/ResponsiveDetailTable';
import DetailFieldCard from '../components/DetailFieldCard';
import { BRAND } from '../brand';

const GROUP_LABELS = { day: 'Daily', week: 'Weekly', month: 'Monthly' };

const COLORS = [BRAND.colors.navy, '#2f77d0', '#2e7d32', '#6a1b9a', '#d32f2f', '#00838f'];

const todayStr = () => new Date().toISOString().slice(0, 10);
const startOfYearStr = () => `${new Date().getFullYear()}-01-01`;

const fmtMoney = (n) => {
  const num = Number(n || 0);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const STATUS_LABELS = {
  in_stock: 'In Stock',
  in_use: 'In Use',
  missed: 'Missed',
  broken: 'Broken',
  discarded: 'Discarded'
};

const InstrumentReports = () => {
  const [from, setFrom] = useState(startOfYearStr());
  const [to, setTo] = useState(todayStr());
  const [group, setGroup] = useState('month');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(startOfYearStr());
  const [draftTo, setDraftTo] = useState(todayStr());
  const [draftGroup, setDraftGroup] = useState('month');
  const [draftSearch, setDraftSearch] = useState('');

  const runReport = async (overrides = {}) => {
    const reportFrom = overrides.from ?? from;
    const reportTo = overrides.to ?? to;
    const reportGroup = overrides.group ?? group;
    const reportSearch = overrides.search ?? search;

    try {
      setLoading(true);
      setError('');
      const params = { from: reportFrom, to: reportTo, group: reportGroup };
      if (reportSearch.trim()) params.search = reportSearch.trim();
      const res = await axiosInstance.get('/reports/instruments', { params });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load instrument report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openFilter = useCallback(() => {
    setDraftFrom(from);
    setDraftTo(to);
    setDraftGroup(group);
    setDraftSearch(search);
    setFilterOpen(true);
  }, [from, to, group, search]);

  const applyFilters = async () => {
    setFrom(draftFrom);
    setTo(draftTo);
    setGroup(draftGroup);
    setSearch(draftSearch);
    setFilterOpen(false);
    await runReport({
      from: draftFrom,
      to: draftTo,
      group: draftGroup,
      search: draftSearch
    });
  };

  const hasActiveFilters =
    from !== startOfYearStr() ||
    group !== 'month' ||
    Boolean(search.trim());

  const headerAction = useMemo(
    () => (
      <Badge invisible={!hasActiveFilters} color="primary" variant="solid" size="sm">
        <Button
          variant="outlined"
          color="primary"
          size="sm"
          startDecorator={<FilterListIcon />}
          onClick={openFilter}
        >
          Filter
        </Button>
      </Badge>
    ),
    [hasActiveFilters, openFilter]
  );

  useSetPageHeaderAction(headerAction);

  const pieData = useMemo(() => {
    const s = data?.summary;
    if (!s) return [];
    return [
      { name: 'In Stock', value: Number(s.in_stock || 0) },
      { name: 'In Use', value: Number(s.in_use || 0) },
      { name: 'Missed', value: Number(s.missed || 0) },
      { name: 'Broken', value: Number(s.broken || 0) },
      { name: 'Discarded', value: Number(s.discarded || 0) }
    ].filter((d) => d.value > 0);
  }, [data]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip size="sm" variant="soft">
          {from} → {to}
        </Chip>
        <Chip size="sm" variant="soft">
          {GROUP_LABELS[group] || group}
        </Chip>
        {search.trim() ? (
          <Chip size="sm" variant="soft" color="primary">
            Search: {search.trim()}
          </Chip>
        ) : null}
      </Box>

      {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

      <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
        <ModalDialog sx={{ width: { xs: '95vw', sm: 480 }, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h3" sx={{ color: 'primary.500', mb: 2 }}>
            Report filters
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl>
              <FormLabel>From</FormLabel>
              <Input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>To</FormLabel>
              <Input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Group</FormLabel>
              <Select value={draftGroup} onChange={(_, v) => setDraftGroup(v || 'month')}>
                <Option value="day">Daily</Option>
                <Option value="week">Weekly</Option>
                <Option value="month">Monthly</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Search</FormLabel>
              <Input
                placeholder="Instrument name, brand..."
                value={draftSearch}
                onChange={(e) => setDraftSearch(e.target.value)}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
              <Button variant="plain" color="neutral" onClick={() => setFilterOpen(false)}>
                Cancel
              </Button>
              <Button
                loading={loading}
                onClick={applyFilters}
                sx={{ backgroundColor: 'primary.500', '&:hover': { backgroundColor: 'primary.600' } }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </ModalDialog>
      </Modal>

      {data?.summary && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Chip color="primary" variant="soft">Total: {data.summary.total_purchased}</Chip>
          <Chip color="success" variant="soft">In Stock: {data.summary.in_stock}</Chip>
          <Chip variant="soft">In Use: {data.summary.in_use}</Chip>
          <Chip color="warning" variant="soft">Missed: {data.summary.missed}</Chip>
          <Chip color="danger" variant="soft">Broken: {data.summary.broken}</Chip>
          <Chip variant="soft">Discarded: {data.summary.discarded}</Chip>
          <Chip variant="outlined">Stock Value: ₹{fmtMoney(data.summary.stock_value)}</Chip>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
        <Card sx={{ p: 2 }}>
          <Typography level="h3" sx={{ mb: 1, color: 'primary.500' }}>Current Status Distribution</Typography>
          <Box sx={{ height: 280 }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography level="body-sm" sx={{ opacity: 0.7, mt: 4, textAlign: 'center' }}>No instrument data</Typography>
            )}
          </Box>
        </Card>

        <Card sx={{ p: 2 }}>
          <Typography level="h3" sx={{ mb: 1, color: 'primary.500' }}>Movements in Range</Typography>
          {data?.totalsInRange && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip size="sm" variant="soft">In Use: {data.totalsInRange.in_use}</Chip>
              <Chip size="sm" variant="soft">Missed: {data.totalsInRange.missed}</Chip>
              <Chip size="sm" variant="soft">Broken: {data.totalsInRange.broken}</Chip>
              <Chip size="sm" variant="soft">Discarded: {data.totalsInRange.discarded}</Chip>
            </Box>
          )}
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.series || []} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="in_use" stackId="a" fill={COLORS[1]} name="In Use" />
                <Bar dataKey="missed" stackId="a" fill={COLORS[4]} name="Missed" />
                <Bar dataKey="broken" stackId="a" fill={COLORS[5]} name="Broken" />
                <Bar dataKey="discarded" stackId="a" fill={COLORS[3]} name="Discarded" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      </Box>

      <Card sx={{ p: 2, mb: 3 }}>
        <Typography level="h3" sx={{ mb: 2, color: 'primary.500' }}>Current Stock by Instrument</Typography>
        <ResponsiveDetailTable
          mobile={
            (data?.byInstrument || []).length === 0 ? (
              <Typography level="body-sm" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                No instruments found
              </Typography>
            ) : (
              (data?.byInstrument || []).map((row) => (
                <DetailFieldCard
                  key={row.instrument_name}
                  title={row.instrument_name}
                  subtitle={[row.brand, row.make].filter(Boolean).join(' / ') || undefined}
                  fields={[
                    { label: 'Total', value: row.total_quantity },
                    { label: 'In Stock', value: row.in_stock_qty },
                    { label: 'In Use', value: row.in_use_qty },
                    { label: 'Missed', value: row.missed_qty },
                    { label: 'Broken', value: row.broken_qty },
                    { label: 'Discarded', value: row.discarded_qty },
                    { label: 'Vendor', value: row.vendor_name || '—' }
                  ]}
                />
              ))
            )
          }
        >
          <Table size="sm" stripe="odd" hoverRow sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
            <thead>
              <tr>
                <th>Instrument</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>In Stock</th>
                <th style={{ textAlign: 'right' }}>In Use</th>
                <th style={{ textAlign: 'right' }}>Missed</th>
                <th style={{ textAlign: 'right' }}>Broken</th>
                <th style={{ textAlign: 'right' }}>Discarded</th>
                <th>Vendor</th>
              </tr>
            </thead>
            <tbody>
              {(data?.byInstrument || []).map((row) => (
                <tr key={row.instrument_name}>
                  <td>
                    <Typography fontWeight={600} sx={{ color: BRAND.colors.navy }}>{row.instrument_name}</Typography>
                    {(row.brand || row.make) && (
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {[row.brand, row.make].filter(Boolean).join(' / ')}
                      </Typography>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{row.total_quantity}</td>
                  <td style={{ textAlign: 'right' }}>{row.in_stock_qty}</td>
                  <td style={{ textAlign: 'right' }}>{row.in_use_qty}</td>
                  <td style={{ textAlign: 'right' }}>{row.missed_qty}</td>
                  <td style={{ textAlign: 'right' }}>{row.broken_qty}</td>
                  <td style={{ textAlign: 'right' }}>{row.discarded_qty}</td>
                  <td>{row.vendor_name || '—'}</td>
                </tr>
              ))}
              {(data?.byInstrument || []).length === 0 && (
                <tr><td colSpan={8} style={{ opacity: 0.7 }}>No instruments found</td></tr>
              )}
            </tbody>
          </Table>
        </ResponsiveDetailTable>
      </Card>

      <Card sx={{ p: 2 }}>
        <Typography level="h3" sx={{ mb: 2, color: 'primary.500' }}>Usage History ({from} to {to})</Typography>
        <ResponsiveDetailTable
          mobile={
            (data?.recentHistory || []).length === 0 ? (
              <Typography level="body-sm" sx={{ opacity: 0.7, textAlign: 'center', py: 2 }}>
                No history in this date range
              </Typography>
            ) : (
              (data?.recentHistory || []).map((h) => (
                <DetailFieldCard
                  key={h.id}
                  title={h.instrument_name}
                  subtitle={formatDisplayDate(h.change_date)}
                  fields={[
                    { label: 'From', value: h.from_status ? STATUS_LABELS[h.from_status] || h.from_status : 'Added' },
                    { label: 'To', value: STATUS_LABELS[h.to_status] || h.to_status },
                    { label: 'Qty', value: h.quantity },
                    { label: 'Remarks', value: h.remarks || '—' },
                    { label: 'By', value: h.created_by_name || '—' }
                  ]}
                />
              ))
            )
          }
        >
          <Table size="sm" stripe="odd" hoverRow sx={{ '& thead th': { backgroundColor: 'primary.500', color: 'white' } }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Instrument</th>
                <th>From</th>
                <th>To</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Remarks</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recentHistory || []).map((h) => (
                <tr key={h.id}>
                  <td>{formatDisplayDate(h.change_date)}</td>
                  <td>{h.instrument_name}</td>
                  <td>{h.from_status ? STATUS_LABELS[h.from_status] || h.from_status : 'Added'}</td>
                  <td>{STATUS_LABELS[h.to_status] || h.to_status}</td>
                  <td style={{ textAlign: 'right' }}>{h.quantity}</td>
                  <td>{h.remarks || '—'}</td>
                  <td>{h.created_by_name || '—'}</td>
                </tr>
              ))}
              {(data?.recentHistory || []).length === 0 && (
                <tr><td colSpan={7} style={{ opacity: 0.7 }}>No history in this date range</td></tr>
              )}
            </tbody>
          </Table>
        </ResponsiveDetailTable>
      </Card>
    </Box>
  );
};

export default InstrumentReports;
